import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useLoanForm } from '@/hooks/useLoanForm';
import { RequiredFieldsSection } from '@/components/loan-form/RequiredFieldsSection';
import { AdvancedFieldsSection } from '@/components/loan-form/AdvancedFieldsSection';
import { FormNavigation } from '@/components/loan-form/FormNavigation';
import {
  sectionNavigation,
  sectionIcons,
} from '@/components/loan-form/constants';
import { LoanStructureSection } from './ui/LoanStructureSection';
import { InterestTermsSection } from './ui/InterestTermsSection';
import { PropertyMetricsSection } from './ui/PropertyMetricsSection';
import { KeyDatesSection } from './ui/KeyDatesSection';
import { LoanStatusSection } from './ui/LoanStatusSection';
import { PropertyLocationsSection } from './ui/PropertyLocationsSection';
import { StepUpTermsSection } from './ui/StepUpTermsSection';
import { PrepaymentTermsSection } from './ui/PrepaymentTermsSection';
import { ForbearanceSection } from './ui/ForbearanceSection';
import { SchedulesSection } from './ui/SchedulesSection';
import type { LoanFormData } from '@/schemas/loanSchema';
import { features } from '@/config/features';
import LoanEditFlatForm from './ui/LoanEditFlatForm';

const Icons = sectionIcons;

interface SectionConfig {
  id: string;
  title: ReactNode;
  icon?: ReactNode | null;
  content: (
    form: UseFormReturn<LoanFormData>,
    addPropertyLocation: () => void,
    removePropertyLocation: (index: number) => void
  ) => ReactNode;
}

const sections: SectionConfig[] = [
  {
    id: 'required',
    title: (
      <div className="flex items-center">
        <DocumentTextIcon className="section-icon-md" />
        Risk Metrics Informed Pricing Scenario
      </div>
    ),
    icon: null,
    content: (form) => <RequiredFieldsSection form={form} />,
  },
  {
    id: 'structure',
    title: 'Loan Structure',
    icon: <Icons.structure className="section-icon-md" />,
    content: (form) => (
      <LoanStructureSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'interest',
    title: 'Interest Rate Terms',
    icon: <Icons.interest className="section-icon-md" />,
    content: (form) => (
      <InterestTermsSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'metrics',
    title: 'Property Financials & Leverage',
    icon: <Icons.metrics className="section-icon-md" />,
    content: (form) => (
      <PropertyMetricsSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'dates',
    title: 'Loan Origination, Maturity and Extension Information',
    icon: <Icons.dates className="section-icon-md" />,
    content: (form) => (
      <KeyDatesSection control={form.control} errors={form.formState.errors} />
    ),
  },
  {
    id: 'status',
    title: 'Loan Status',
    icon: <Icons.status className="section-icon-md" />,
    content: (form) => (
      <LoanStatusSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'locations',
    title: 'Property Location(s)',
    icon: <Icons.locations className="section-icon-md" />,
    content: (form, addPropertyLocation, removePropertyLocation) => (
      <PropertyLocationsSection
        control={form.control}
        errors={form.formState.errors}
        watch={form.watch}
        addPropertyLocation={addPropertyLocation}
        removePropertyLocation={removePropertyLocation}
      />
    ),
  },
  {
    id: 'stepup',
    title: 'Step-up Coupon Terms',
    icon: <Icons.stepup className="section-icon-md" />,
    content: (form) => (
      <StepUpTermsSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'prepay',
    title: 'Prepayment Terms',
    icon: <Icons.prepay className="section-icon-md" />,
    content: (form) => (
      <PrepaymentTermsSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'forbearance',
    title: 'Forbearance',
    icon: <Icons.forbearance className="section-icon-md" />,
    content: (form) => (
      <ForbearanceSection
        control={form.control}
        errors={form.formState.errors}
      />
    ),
  },
  {
    id: 'schedules',
    title: 'Schedule Modifications',
    icon: <Icons.schedules className="section-icon-md" />,
    content: (form) => (
      <SchedulesSection control={form.control} errors={form.formState.errors} />
    ),
  },
  {
    id: 'advanced',
    title: 'Additional Identifiers',
    icon: <Icons.advanced className="section-icon-md" />,
    content: (form) => <AdvancedFieldsSection form={form} />,
  },
];

export default function LoanFormPage() {
  const {
    form,
    isSubmitting,
    isLoading,
    isEditMode,
    onSubmit,
    addPropertyLocation,
    removePropertyLocation,
  } = useLoanForm();

  const [expandedSections, setExpandedSections] = useState<string[]>([
    'required',
  ]);
  const [activeSectionId, setActiveSectionId] = useState<string>('required');
  const sectionIds = useMemo(() => sectionNavigation.map((s) => s.id), []);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const activeUpdateTimeout = useRef<number | null>(null);

  function scheduleActiveUpdate() {
    if (activeUpdateTimeout.current) {
      window.clearTimeout(activeUpdateTimeout.current);
      activeUpdateTimeout.current = null;
    }

    activeUpdateTimeout.current = window.setTimeout(() => {
      const positions = sectionIds
        .filter((id) => expandedSections.includes(id))
        .map((id) => {
          const itemEl = document.getElementById(id);
          const contentEl = itemEl?.querySelector(
            '[data-test-id="accordion-content-wrapper"]'
          ) as HTMLElement | null;
          const top = contentEl
            ? contentEl.getBoundingClientRect().top
            : Number.POSITIVE_INFINITY;
          const bottom = contentEl
            ? contentEl.getBoundingClientRect().bottom
            : Number.POSITIVE_INFINITY;
          return { id, top, bottom };
        });

      if (positions.length === 0) return;

      const targetY = window.innerHeight * 0.4;
      let best = positions[0];
      let bestDist = Math.min(
        Math.abs(best.top - targetY),
        Math.abs(best.bottom - targetY)
      );

      for (let i = 1; i < positions.length; i++) {
        const p = positions[i];
        const dist = Math.min(
          Math.abs(p.top - targetY),
          Math.abs(p.bottom - targetY)
        );
        if (dist < bestDist) {
          best = p;
          bestDist = dist;
        }
      }

      if (best && best.id !== activeSectionId) {
        setActiveSectionId(best.id);
      }
    }, 500);
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      () => {
        scheduleActiveUpdate();
      },
      {
        root: null,
        rootMargin: '0% 0% 0% 0%',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => {
      observer.disconnect();
      if (activeUpdateTimeout.current) {
        window.clearTimeout(activeUpdateTimeout.current);
        activeUpdateTimeout.current = null;
      }
    };
  }, [sectionIds, expandedSections, activeSectionId]);

  function handleNavigate(id: string) {
    if (!expandedSections.includes(id)) {
      setExpandedSections((prev) => [...prev, id]);
    }
    setActiveSectionId(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Show loading spinner while loading loan data
  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-gray-600">Loading loan data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container flex flex-col md:flex-row loanform-gradient-bg"
      data-testid="loan-form-page"
    >
      {features.variantAEdit.enabled && isEditMode ? (
        <LoanEditFlatForm />
      ) : (
      <>
      {/* Section Navigation */}
      <FormNavigation
        sections={sectionNavigation}
        expandedSections={expandedSections}
        activeSectionId={activeSectionId}
        onNavigate={handleNavigate}
      />

      {/* Main Form */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="page-inner flex-1 card-glass loanform-section loanform-card-bg loanform-border"
        data-testid="loan-form"
      >
        <div>
          <h1 className="text-2xl font-bold mb-2" data-testid="form-title">
            Loan Details & Pricing Assumptions
          </h1>
          <p className="text-gray-600 mb-8" data-testid="form-description">
            {isEditMode
              ? 'Update the loan information below'
              : 'Complete all required fields and optionally fill in additional information'}
          </p>
        </div>

        {/* All Sections as Accordions */}
        <Accordion
          type="multiple"
          value={expandedSections}
          onValueChange={(val) =>
            setExpandedSections(Array.isArray(val) ? (val as string[]) : [])
          }
        >
          {(() => {
            return sections.map((s) => (
              <AccordionItem
                key={s.id}
                value={s.id}
                id={s.id}
                ref={(el) => {
                  sectionRefs.current[s.id] = el;
                }}
              >
                <AccordionTrigger>
                  <div className="flex items-center gap-2 text-2xl font-medium">
                    {s.icon}
                    {s.title}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {s.content(form, addPropertyLocation, removePropertyLocation)}
                </AccordionContent>
              </AccordionItem>
            ));
          })()}
        </Accordion>

        {/* Action Buttons */}
        <div className="flex justify-end mt-8">
          <Button
            variant="default"
            type="submit"
            disabled={isSubmitting}
            className="btn-accent loanform-btn loanform-btn-primary loanform-accent"
            data-testid="submit-button"
          >
            {isSubmitting
              ? isEditMode
                ? 'Updating Loan...'
                : 'Creating Loan...'
              : isEditMode
                ? 'Update Loan'
                : 'Save Loan Data'}
          </Button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}
